import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { formatNumber } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { FC_TRANSFER_DISPLAY_FALLBACK } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';

/**
 * Human-readable "{n} case(s) / {n} loose unit(s)" line for tables and summaries.
 *
 * @param {unknown} casesRaw
 * @param {unknown} looseRaw
 * @returns {string}
 */
export function formatCasesAndLooseSummary(casesRaw, looseRaw) {
    const c =
        casesRaw === undefined || casesRaw === null || casesRaw === ''
            ? FC_TRANSFER_DISPLAY_FALLBACK
            : formatNumber(casesRaw, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    const l =
        looseRaw === undefined || looseRaw === null || looseRaw === ''
            ? FC_TRANSFER_DISPLAY_FALLBACK
            : formatNumber(looseRaw, { maximumFractionDigits: 0, minimumFractionDigits: 0 });

    let caseNoun = 'cases';
    if (c !== FC_TRANSFER_DISPLAY_FALLBACK && Number(casesRaw) === 1) {
        caseNoun = 'case';
    }

    let looseNoun = 'loose units';
    if (l !== FC_TRANSFER_DISPLAY_FALLBACK && Number(looseRaw) === 1) {
        looseNoun = 'loose unit';
    }

    return `${c} ${caseNoun} / ${l} ${looseNoun}`;
}

/**
 * Expected shipped qty summary.
 * Uses explicit expected fields when present, then legacy/shipped keys.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatExpectedQtySummary(item) {
    const cases =
        item?.expectedNumCases ??
        item?.expected_num_cases ??
        item?.numCases ??
        item?.num_cases ??
        item?.shippedNumCases;
    const loose =
        item?.expectedNumLooseUnits ??
        item?.expected_num_loose_units ??
        item?.numLooseUnits ??
        item?.num_loose_units ??
        item?.shippedNumLooseUnits;
    return formatCasesAndLooseSummary(cases, loose);
}

/**
 * Received qty summary for display.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatReceivedQtySummary(item) {
    const cases = item?.receivedNumCases ?? item?.received_num_cases;
    const loose = item?.receivedNumLooseUnits ?? item?.received_num_loose_units;
    return formatCasesAndLooseSummary(cases, loose);
}

/**
 * Expected BBD: canonical expected fields first, then outbound snapshot fallbacks until backend ships explicit keys.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatExpectedBbd(item) {
    const raw =
        item?.expectedExpiresOn ??
        item?.expected_expires_on ??
        item?.sourceExpiresOnSnapshot ??
        item?.source_expires_on_snapshot ??
        item?.effectiveExpiresOn;
    return formatPurchaseOrderDate(raw, FC_TRANSFER_DISPLAY_FALLBACK);
}

/**
 * Received BBD: persisted receipt fields first, then effective “current line” value (pre-finalize drafts).
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatReceivedBbd(item) {
    const raw =
        item?.receivedExpiresOn ??
        item?.received_expires_on ??
        item?.effectiveExpiresOn ??
        item?.sourceExpiresOnSnapshot ??
        item?.source_expires_on_snapshot;
    return formatPurchaseOrderDate(raw, FC_TRANSFER_DISPLAY_FALLBACK);
}

/**
 * Expected count/case: canonical expected fields first, then snapshot fallbacks.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatExpectedUnitsPerCase(item) {
    const raw =
        item?.expectedUnitsPerCase ??
        item?.expected_units_per_case ??
        item?.sourceUnitsPerCaseSnapshot ??
        item?.source_units_per_case_snapshot ??
        item?.effectiveUnitsPerCase;
    if (raw === undefined || raw === null || raw === '') {
        return FC_TRANSFER_DISPLAY_FALLBACK;
    }
    return formatNumber(raw, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/**
 * Received count/case.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatReceivedUnitsPerCase(item) {
    const raw =
        item?.receivedUnitsPerCase ??
        item?.received_units_per_case ??
        item?.effectiveUnitsPerCase ??
        item?.sourceUnitsPerCaseSnapshot ??
        item?.source_units_per_case_snapshot;
    if (raw === undefined || raw === null || raw === '') {
        return FC_TRANSFER_DISPLAY_FALLBACK;
    }
    return formatNumber(raw, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}
